<template>
  <b-overlay center :show="isTabLoading" rounded="sm">
    <div class="mx-1 mt-4 mb-2" v-show="!isTabLoading">
      <h6>Persone fisiche</h6>
      <table-registries
        @unify="onUnify"
        :fields="fields"
        :repository="repository"
        :resource="resource"
        :filterName="filterName"
        :isTableInModal="true"
        :ref="tableRef"
        :filterOn="filterOn"
        noPagination
        noActions
      ></table-registries>
      <h6 class="mt-4">Persone giuridiche</h6>
      <table-registries
        @unify="onUnify"
        :fields="fields"
        :repository="repository"
        :resource="resource"
        :filterName="filterNamePG"
        :isTableInModal="true"
        :ref="tableRefPG"
        :filterOn="filterOn"
        noPagination
        noActions
      ></table-registries>
    </div>
    <template #overlay>
      <div class="text-center">
        <div class="custom-loader"></div>
        <p class="loading-text">Operazione in corso...</p>
      </div>
    </template>
  </b-overlay>
</template>

<script>
import ShortcutMixin from "@/mixins/ShortcutMixin";
import { RepositoryFactory } from "@/repositories/RepositoryFactory";
import DetailTabAutofetchTableMixin from "@/mixins/DetailTabAutofetchTableMixin";
import TableRegistries from "@/components/tables/Registries";
import { mapActions } from "vuex";

export default {
  mixins: [ShortcutMixin, DetailTabAutofetchTableMixin],
  name: "Duplicated",
  data() {
    return {
      repository: "registry",
      resource: "registries",
      filterName: "filterRegistryDuplicated",
      filterNamePG: "filterRegistryDuplicatedPG",
      tableRef: "RegistryDuplicatedTableRef",
      tableRefPG: "RegistryDuplicatedPGTableRef",
      filterOn: {
        byRelations: [
          "byRegistryGroup",
          "byInsurancePolicy",
          "byBroker",
          "byTask",
          "byDocument",
          "byPrivacy",
        ],
      },
      fields: [
        {
          key: "selezionato",
          label: "",
          sortable: false,
        },
        {
          key: "customer",
          value: (item) =>
            item.status_registry.value === 1
              ? item.attributables?.CNAM
              : `${item.attributables?.SURN ?? ""} ${item.attributables?.NAME ?? ""}`,
          label: this.getDictionary("customer_name"),
          sortable: true,
          sortKey: "title",
        },
        {
          key: "status_registry",
          label: this.getDictionary("status_registry", "registry"),
          value: (item) => (item.status_registry.value === 1 ? "G" : "F"),
          sortable: false,
        },
        {
          key: "attributables.NINO",
          value: "attributables.NINO",
          label: this.getDictionary("attribute_NINO_value", "registry"),
          sortable: true,
          sortKey: "byAttribute.NINO",
        },
        {
          key: "attributables.CREG",
          value: "attributables.CREG",
          label: this.getDictionary("attribute_CREG_value", "registry"),
          sortable: true,
          sortKey: "byAttribute.CREG",
        },
        {
          key: "insurance_policy",
          value: (item) =>
            item.insurance_policies && item.insurance_policies.length
              ? item.insurance_policies.length
              : "0",
          label: this.getDictionary("insurance_policies"),
        },
        {
          key: "primary_group",
          value: (item) =>
            item.registry_groups && item.registry_groups.length
              ? item.registry_groups.find(
                  (rg) => rg.pivot.primary.value === "Y"
                )?.code || ""
              : "",
          label: this.getDictionary("Gruppo Primario"),
        },
        {
          key: "broker",
          value: (item) =>
            item.insurance_policies
              ? item.insurance_policies
                  .filter((e) => e.broker && e.broker.status_broker.value === 0)
                  .filter((e) =>
                    e.status_policy && [0, 1, 2].includes(e.status_policy.value)
                  )
                  .map((e) => e.broker.attributables.BUID)
                  .filter((v, i, arr) => arr.indexOf(v) === i)
                  .join(", ")
              : "",
          label: this.getDictionary("salesman"),
        },
      ],
    };
  },
  computed: {
    status() {
      return this.dbRowData?.status_registry?.value;
    },
    // Si cerca per codice fiscale, sia dalle persone fisiche sia dalle giuridiche (ditte
    // individuali); la partita IVA solo se il codice fiscale manca.
    // I valori tutti a zero sono segnaposto (es. società estere) e valgono come assenti.
    searchCriteria() {
      const attr = this.dbRowData?.attributables || {};
      const isValid = (value) => !!value && !/^0+$/.test(String(value).trim());
      if (isValid(attr.NINO)) {
        return { NINO: attr.NINO };
      }
      if (isValid(attr.CREG)) {
        return { CREG: attr.CREG };
      }
      return null;
    },
  },
  methods: {
    // Due tabelle, una per tipo: "Mantieni" unifica solo le righe della propria tabella,
    // quindi non si possono mai unificare persone fisiche con persone giuridiche.
    onSearch() {
      const tablePF = this.$refs[this.tableRef];
      const tablePG = this.$refs[this.tableRefPG];
      if (tablePF === undefined || tablePG === undefined) {
        this.isTabLoading = false;
        return;
      }
      if (!this.searchCriteria) {
        tablePF.items = [];
        tablePG.items = [];
        this.isTabLoading = false;
        return;
      }
      this.isTabLoading = true;
      Promise.all([
        this.fetchTable(this.filterName, tablePF, 0),
        this.fetchTable(this.filterNamePG, tablePG, 1),
      ])
        .then(() => {
          // nella tabella dello stesso tipo della scheda una sola riga è la scheda stessa
          const own = this.status === 1 ? tablePG : tablePF;
          if (own.items.length <= 1) {
            own.items = [];
          }
          this.isTabLoading = false;
        })
        .catch((error) => {
          this.isTabLoading = false;
          let errMsg = this.$getErrorMessage(error);
          this.$showSnackbar({ preset: "error", text: errMsg });
        });
    },
    fetchTable(name, tableRef, status) {
      const criteria = {
        byAttribute: { ...this.searchCriteria, status_registry: status },
      };
      this.saveFilterByName({ name, criteria });
      return tableRef.fetch().finally(() => this.removeFilterByName(name));
    },
    onUnify(payload) {
      const { selectedRegistryId, allTableItems } = payload;
      const sourceRegistryIds = allTableItems
        .filter((item) => item.id !== selectedRegistryId)
        .map((item) => item.id);
      const Repo = RepositoryFactory.get("registry");
      this.isTabLoading = true;
      Repo.unify({ targetRegistryId: selectedRegistryId, sourceRegistryIds })
        .then(() => {
          this.$showSnackbar({
            preset: "success",
            text: "Anagrafiche unificate correttamente",
          });
          this.isTabLoading = false;
          this.$emit("fetch");
          this.$emit("closeModal");
        })
        .catch((error) => {
          this.isTabLoading = false;
          let errMsg = this.$getErrorMessage(error);
          this.$showSnackbar({ preset: "error", text: errMsg });
        });
    },
    ...mapActions("filters", {
      saveFilterByName: "saveByName",
      removeFilterByName: "removeByName",
    }),
  },
  components: {
    TableRegistries,
  },
  props: {
    resourceId: Number,
    dbRowData: Object,
  },
};
</script>

<style lang="scss" scoped>
:deep(.actions) {
  background-color: transparent !important;
}
</style>
